import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Download,
  RefreshCw,
  Filter,
  Search,
  Edit2,
  AlertTriangle,
  DollarSign,
  Users,
  TrendingUp,
  Activity,
  CheckSquare,
  Square
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = '';

const EnhancedDepositMonitoring = () => {
  const { toast } = useToast();
  const [deposits, setDeposits] = useState([]);
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [adjustedAmount, setAdjustedAmount] = useState('');
  const [selectedDeposits, setSelectedDeposits] = useState([]);
  const [stats, setStats] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchDeposits();
    fetchStats();
  }, []);

  useEffect(() => {
    // Auto-refresh every 30 seconds if enabled
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchDeposits(true); // Silent refresh
        fetchStats();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  useEffect(() => {
    // Filter deposits based on status, currency, and search
    let filtered = deposits;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(d => d.status === statusFilter);
    }

    if (currencyFilter !== 'all') {
      filtered = filtered.filter(d => d.currency.toLowerCase() === currencyFilter.toLowerCase());
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d =>
        d.username.toLowerCase().includes(query) ||
        d.email.toLowerCase().includes(query) ||
        d.deposit_id.toLowerCase().includes(query)
      );
    }

    setFilteredDeposits(filtered);
  }, [deposits, statusFilter, currencyFilter, searchQuery]);

  const fetchDeposits = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const response = await axios.get(`${API_URL}/api/admin/deposits/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setDeposits(response.data.deposits);
      }
    } catch (error) {
      if (!silent) {
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to fetch deposits',
          variant: 'destructive'
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const response = await axios.get(`${API_URL}/api/admin/deposits/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAction = async (depositId, action, notes = '', adjustAmount = null) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const payload = {
        action: action,
        admin_notes: notes || undefined
      };

      if (action === 'adjust' && adjustAmount) {
        payload.adjusted_amount = parseFloat(adjustAmount);
      }

      const response = await axios.post(
        `${API_URL}/api/admin/deposits/${depositId}/action`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: response.data.message,
        });
        
        setShowDetailModal(false);
        setShowAdjustModal(false);
        setSelectedDeposit(null);
        setAdminNotes('');
        setAdjustedAmount('');
        fetchDeposits();
        fetchStats();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process deposit',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedDeposits.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select deposits to process',
        variant: 'destructive'
      });
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('adminToken') || localStorage.getItem('secure_admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/deposits/bulk-action`,
        {
          deposit_ids: selectedDeposits,
          action: action,
          admin_notes: adminNotes || undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast({
          title: 'Success',
          description: response.data.message,
        });
        
        setSelectedDeposits([]);
        setAdminNotes('');
        fetchDeposits();
        fetchStats();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process bulk action',
        variant: 'destructive'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelectDeposit = (depositId) => {
    setSelectedDeposits(prev =>
      prev.includes(depositId)
        ? prev.filter(id => id !== depositId)
        : [...prev, depositId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDeposits.length === filteredDeposits.filter(d => d.status === 'pending').length) {
      setSelectedDeposits([]);
    } else {
      setSelectedDeposits(filteredDeposits.filter(d => d.status === 'pending').map(d => d.deposit_id));
    }
  };

  const exportToCSV = () => {
    const csvData = filteredDeposits.map(d => ({
      'Deposit ID': d.deposit_id,
      'User': d.username,
      'Email': d.email,
      'Currency': d.currency,
      'Amount': d.amount,
      'Network': d.network,
      'Status': d.status,
      'Date': new Date(d.created_at).toLocaleString()
    }));

    const csv = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `deposits_${new Date().toISOString()}.csv`;
    a.click();
  };

  const viewDetails = (deposit) => {
    setSelectedDeposit(deposit);
    setShowDetailModal(true);
  };

  const openAdjustModal = (deposit) => {
    setSelectedDeposit(deposit);
    setAdjustedAmount(deposit.amount.toString());
    setShowAdjustModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      completed: 'bg-green-500/20 text-green-400 border-green-500/50',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/50'
    };

    const icons = {
      pending: <Clock className="w-4 h-4" />,
      completed: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />
    };

    return (
      <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.toUpperCase()}
      </span>
    );
  };

  const uniqueCurrencies = ['all', ...new Set(deposits.map(d => d.currency))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                <Activity className="w-8 h-8 text-cyan-400" />
                Enhanced Deposit Monitoring
              </h1>
              <p className="text-gray-400">Comprehensive deposit management with full admin controls</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  autoRefresh
                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                    : 'bg-slate-800 text-gray-400 border border-slate-700'
                }`}
              >
                {autoRefresh ? '🟢 Auto-Refresh ON' : '⚫ Auto-Refresh OFF'}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/50 p-4 rounded-lg border border-cyan-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Total Deposits</div>
                  <div className="text-2xl font-bold text-white">{stats.total_deposits}</div>
                </div>
                <Activity className="w-8 h-8 text-cyan-400" />
              </div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-yellow-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Pending</div>
                  <div className="text-2xl font-bold text-yellow-400">{stats.pending_count}</div>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-green-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Approved</div>
                  <div className="text-2xl font-bold text-green-400">{stats.approved_count}</div>
                </div>
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg border border-cyan-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-400">Total Approved</div>
                  <div className="text-2xl font-bold text-cyan-400">${stats.total_approved_amount.toFixed(2)}</div>
                </div>
                <DollarSign className="w-8 h-8 text-cyan-400" />
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by user, email, or deposit ID..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex gap-2">
              {['all', 'pending', 'completed', 'rejected'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    statusFilter === status
                      ? 'bg-cyan-500 text-white'
                      : 'bg-slate-900 text-gray-400 hover:bg-slate-800'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Currency Filter */}
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
            >
              {uniqueCurrencies.map(currency => (
                <option key={currency} value={currency}>
                  {currency === 'all' ? 'All Currencies' : currency.toUpperCase()}
                </option>
              ))}
            </select>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={() => { fetchDeposits(); fetchStats(); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 rounded-lg text-white transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 hover:bg-green-500/20 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedDeposits.length > 0 && (
          <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="text-white font-semibold">
                {selectedDeposits.length} deposit(s) selected
              </div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes (optional)..."
                  className="px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                />
                <button
                  onClick={() => handleBulkAction('approve')}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve All
                </button>
                <button
                  onClick={() => handleBulkAction('reject')}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Reject All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deposits Table */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-400">Loading deposits...</div>
          ) : filteredDeposits.length === 0 ? (
            <div className="p-8 text-center text-gray-400">No deposits found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50 border-b border-slate-700">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <button onClick={toggleSelectAll}>
                        {selectedDeposits.length === filteredDeposits.filter(d => d.status === 'pending').length && filteredDeposits.filter(d => d.status === 'pending').length > 0 ? (
                          <CheckSquare className="w-5 h-5 text-cyan-400" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">User</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Currency</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Network</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDeposits.map((deposit) => (
                    <tr key={deposit.deposit_id} className="border-b border-slate-700 hover:bg-slate-800/30">
                      <td className="px-4 py-4">
                        {deposit.status === 'pending' && (
                          <button onClick={() => toggleSelectDeposit(deposit.deposit_id)}>
                            {selectedDeposits.includes(deposit.deposit_id) ? (
                              <CheckSquare className="w-5 h-5 text-cyan-400" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-white font-semibold">{deposit.username}</div>
                          <div className="text-xs text-gray-400">{deposit.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-mono font-bold text-cyan-400">{(deposit.currency || '').toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="font-bold text-white">{deposit.amount}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-300">{deposit.network}</span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-sm text-gray-300">
                          {new Date(deposit.created_at).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(deposit.status)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => viewDetails(deposit)}
                            className="p-2 bg-cyan-500/10 border border-cyan-500/30 rounded text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {deposit.status === 'pending' && (
                            <button
                              onClick={() => openAdjustModal(deposit)}
                              className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-yellow-400 hover:bg-yellow-500/20 transition-colors"
                              title="Adjust Amount"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Deposit Details</h2>
                <button
                  onClick={() => { setShowDetailModal(false); setSelectedDeposit(null); setAdminNotes(''); }}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {/* Deposit Info */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-400">Deposit ID</div>
                    <div className="font-mono text-white">{selectedDeposit.deposit_id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Status</div>
                    <div>{getStatusBadge(selectedDeposit.status)}</div>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">User Information</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Username</div>
                      <div className="text-white">{selectedDeposit.username}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Email</div>
                      <div className="text-white">{selectedDeposit.email}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">Deposit Information</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Currency</div>
                      <div className="font-mono font-bold text-cyan-400">{(selectedDeposit.currency || '').toUpperCase()}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Network</div>
                      <div className="text-white">{selectedDeposit.network}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Amount</div>
                      <div className="text-2xl font-bold text-white">{selectedDeposit.amount}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Date</div>
                      <div className="text-white">{new Date(selectedDeposit.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>

                {selectedDeposit.wallet_address && (
                  <div className="border-t border-slate-700 pt-4">
                    <div className="text-sm text-gray-400 mb-2">Wallet Address</div>
                    <div className="font-mono text-xs text-white bg-slate-800 p-3 rounded">
                      {selectedDeposit.wallet_address}
                    </div>
                  </div>
                )}

                {selectedDeposit.screenshot_path && (
                  <div className="border-t border-slate-700 pt-4">
                    <div className="text-sm text-gray-400 mb-2">Payment Screenshot</div>
                    <img 
                      src={`${API_URL}${selectedDeposit.screenshot_path}`}
                      alt="Payment Screenshot"
                      className="max-w-full rounded-lg border border-slate-700"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              {/* Admin Actions (only for pending deposits) */}
              {selectedDeposit.status === 'pending' && (
                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-3">Admin Actions</div>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add notes for the user (optional)..."
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none mb-4"
                    rows="3"
                  />

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleAction(selectedDeposit.deposit_id, 'approve', adminNotes)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Approve & Credit'}
                    </button>
                    <button
                      onClick={() => handleAction(selectedDeposit.deposit_id, 'reject', adminNotes)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-5 h-5" />
                      {actionLoading ? 'Processing...' : 'Reject'}
                    </button>
                  </div>
                </div>
              )}

              {/* Processed Info (for completed/rejected) */}
              {selectedDeposit.status !== 'pending' && (
                <div className="border-t border-slate-700 pt-4">
                  <div className="text-sm text-gray-400 mb-2">Processing Information</div>
                  <div className="space-y-2 text-sm">
                    {selectedDeposit.processed_by_username && (
                      <div>
                        <span className="text-gray-500">Processed by:</span>
                        <span className="text-white ml-2">{selectedDeposit.processed_by_username}</span>
                      </div>
                    )}
                    {selectedDeposit.processed_at && (
                      <div>
                        <span className="text-gray-500">Processed at:</span>
                        <span className="text-white ml-2">{new Date(selectedDeposit.processed_at).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedDeposit.admin_notes && (
                      <div>
                        <div className="text-gray-500">Admin Notes:</div>
                        <div className="text-white bg-slate-800 p-2 rounded mt-1">{selectedDeposit.admin_notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Adjust Amount Modal */}
      {showAdjustModal && selectedDeposit && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Edit2 className="w-6 h-6 text-yellow-400" />
                  Adjust Deposit Amount
                </h2>
                <button
                  onClick={() => { setShowAdjustModal(false); setSelectedDeposit(null); setAdjustedAmount(''); setAdminNotes(''); }}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-400 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-semibold">Amount Adjustment</span>
                  </div>
                  <p className="text-sm text-gray-300">
                    You are about to adjust the deposit amount. This will update the deposit record before approval.
                  </p>
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-2">Original Amount</div>
                  <div className="text-2xl font-bold text-white">
                    {selectedDeposit.amount} {selectedDeposit.currency.toUpperCase()}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-2">New Amount</div>
                  <input
                    type="number"
                    value={adjustedAmount}
                    onChange={(e) => setAdjustedAmount(e.target.value)}
                    placeholder="Enter new amount..."
                    step="0.01"
                    min="0"
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                  />
                </div>

                <div>
                  <div className="text-sm text-gray-400 mb-2">Reason for Adjustment</div>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Explain why the amount was adjusted..."
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowAdjustModal(false); setSelectedDeposit(null); setAdjustedAmount(''); setAdminNotes(''); }}
                  className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAction(selectedDeposit.deposit_id, 'adjust', adminNotes, adjustedAmount)}
                  disabled={actionLoading || !adjustedAmount || parseFloat(adjustedAmount) <= 0}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  <Edit2 className="w-5 h-5" />
                  {actionLoading ? 'Adjusting...' : 'Adjust Amount'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDepositMonitoring;